import React from 'react';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Loading from 'Components/Loading';
import pMinDelay from 'p-min-delay';
import ApplicationLayout from 'Components/ApplicationLayout';
import { isWebviewRenderType } from 'Utils/webviewMode';
import { useFastDepositABTest } from './FastDeposit/useFastDepositABTest';

const Finance = loadable(() => import('./Finance'), {
  fallback: <Loading />
});

const WithdrawMoney = loadable(() => import('./WithdrawMoney'), {
  fallback: <Loading />
});

const Bilyoncard = loadable(() => import('./Bilyoncard'), {
  fallback: <Loading />
});

const DepositMoney = loadable(() => import('./DepositMoney'), {
  fallback: <Loading />
});

const PaymentOptions = loadable(() => pMinDelay(import('./DepositMoney/PaymentOptions'), 300), {
  fallback: <Loading />
});

const FastDeposit = loadable(() => import('./FastDeposit'), {
  fallback: <Loading />
});

const AllTransactions = loadable(() => import('./FastDeposit/AllTransactions'), {
  fallback: <Loading />
});

const BankTransactions = loadable(() => import('./BankTransactions'), {
  fallback: <Loading />
});

const AddNewAccount = loadable(() => import('./BankTransactions/AddNewAccount'), {
  fallback: <Loading />
});

const Activities = loadable(() => import('./Activities'), {
  fallback: <Loading />
});

const CommunicationSettings = loadable(() => import('../Settings/CommunicationSettings'), {
  fallback: <Loading />
});

const AsyncFinance = props => {
  const { linkName, subLinkName, thirdLinkName } = props.match.params;
  const isFastDepositRoute = linkName === 'hizli-para-yatir' || (linkName === 'para-yatir' && subLinkName === 'hizli-para-yatir');

  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const { isFastDepositEnabled, isLoading } = useFastDepositABTest();

  let bgClass = 'bg-theme-primary';

  if (linkName === 'pending-deposit') {
    bgClass = 'bg-pending-gray';
  } else if (linkName?.includes('para-yatir') || linkName?.includes('para-cek')) {
    bgClass = 'bg-deposit-money';
  }

  let pageConfig = {
    headerType: 'full',
    footerType: 'full',
    helmet: {
      bodyAttributes: {
        class: `${ props.userAgent.device?.type } ${bgClass} hide-betslip hide-sticky-navigation`
      },
      title: 'Bilyoner.com | Hesabım | Finansal İşlemler'
    }
  };

  let pageContent = '';

  // eslint-disable-next-line react/prop-types
  const isWebview = isWebviewRenderType(props.location.search);

  if (linkName === 'para-yatir' && isWebview) {
    pageConfig.headerType = props.userAgent.device?.type === 'desktop' ? 'full' : 'mobile-none';
    pageConfig.footerType = props.userAgent.device?.type === 'desktop' ? 'full' : 'none';
  }

  if (isFastDepositRoute && isLoading) {
    return (
      <ApplicationLayout { ...pageConfig } { ...props }>
        <Loading />
      </ApplicationLayout>
    );
  }

  switch (linkName) {
    case 'para-cek':
      pageContent = <WithdrawMoney { ...props } headerColor="white"/>;
      pageConfig.helmet.title = 'Bilyoner.com | Hesabım | Para Çekme';
      break;
    case 'bilyonkart':
      pageContent = <Bilyoncard { ...props }/>;
      pageConfig.helmet.title = 'Bilyoner.com | Hesabım | Bilyonkart Yükle';
      break;
    case 'para-yatir':
      if (subLinkName === 'hizli-para-yatir') {
        if (!isFastDepositEnabled) {
          pageContent = isAuthenticated ? <Redirect to="/hesabim/para-yatir" /> : <Redirect to="/giris-yap" />;
        } else if (thirdLinkName === 'son-islemlerim') {
          pageContent = <AllTransactions { ...props } />;
          pageConfig.helmet.title = 'Bilyoner.com | Hesabım | Hızlı Para Yatır | Son İşlemlerim';
        } else {
          pageContent = <FastDeposit { ...props } />;
          pageConfig.helmet.title = 'Bilyoner.com | Hesabım | Hızlı Para Yatır';
        }
      } else if (subLinkName === 'odeme-secenekleri') {
        pageContent = <PaymentOptions { ...props } headerColor="white" />;
        pageConfig.helmet.title = 'Bilyoner.com | Hesabım | Ödeme Seçenekleri';
      } else {
        pageContent = <DepositMoney { ...props } headerColor="white"/>;
        pageConfig.helmet.title = 'Bilyoner.com | Hesabım | Para Yatırma';
      }
      break;
    case 'hizli-para-yatir':
      if (!isFastDepositEnabled) {
        pageContent = isAuthenticated ? <Redirect to="/hesabim/para-yatir" /> : <Redirect to="/giris-yap" />;
      } else if (subLinkName === 'son-islemlerim') {
        pageContent = <AllTransactions { ...props } />;
        pageConfig.helmet.title = 'Bilyoner.com | Hesabım | Hızlı Para Yatır | Son İşlemlerim';
      } else {
        pageContent = <FastDeposit { ...props } />;
        pageConfig.helmet.title = 'Bilyoner.com | Hesabım | Hızlı Para Yatır';
      }
      break;
    case 'banka-islemlerim':
      pageContent = <BankTransactions { ...props }/>;
      pageConfig.helmet.title = 'Bilyoner.com | Hesabım | Banka İşlemlerim';
      break;
    case 'yeni-hesap-ekle':
      pageContent = <AddNewAccount { ...props }/>;
      pageConfig.helmet.title = 'Bilyoner.com | Hesabım | Banka İşlemlerim | Yeni Hesap Ekle';
      break;
    case 'hesap-hareketlerim':
      pageContent = <Activities { ...props }/>;
      pageConfig.helmet.title = 'Bilyoner.com | Hesabım | Hesap Hareketlerim';
      break;
    case 'iletisim-izinleri':
      pageContent = <CommunicationSettings { ...props } />;
      pageConfig.helmet.title = 'Bilyoner.com | Hesabım | İletişim İzinleri';
      break;
    default:
      pageContent = <Finance { ...props }/>;
  }

  return (
    <ApplicationLayout { ...pageConfig } { ...props }>
      { pageContent }
    </ApplicationLayout>
  );
};

AsyncFinance.propTypes = {
  userAgent: PropTypes.object,
  match: PropTypes.object
};

export default AsyncFinance;
